
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, Syringe, Smile, Brackets, Sun, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import smilebirdLogo from "@/assets/smilebird-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const location = useLocation();
  
  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
    setExpandedCategory(null);
  }, [location.pathname]);
  
  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleCategory = (category: string) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(category);
    }
  };

  // Organized services by categories
  const serviceCategories = [
    {
      category: "Pain Relief",
      icon: <Syringe className="h-4 w-4 mr-2" />,
      services: [
        { title: "Painless RCT", href: "/services/painless-rct-in-nagpur" },
        { title: "Wisdom Tooth Removal", href: "/services/wisdom-tooth-removal-nagpur" },
        { title: "TMJ/TMD", href: "/services/tmj-tmd-in-nagpur" },
      ]
    },
    {
      category: "Preventive Care",
      icon: <Stethoscope className="h-4 w-4 mr-2" />,
      services: [
        { title: "Gum Cleaning", href: "/services/gum-cleaning-in-nagpur" },
        { title: "Teeth Cleaning", href: "/services/teeth-cleaning-in-nagpur" },
      ]
    },
    {
      category: "Cosmetic Dentistry",
      icon: <Smile className="h-4 w-4 mr-2" />,
      services: [
        { title: "Orthodontics", href: "/services/orthodontics-in-nagpur" },
        { title: "Invisalign", href: "/services/invisalign-in-nagpur" },
        { title: "Smile Makeover", href: "/services/smile-makeover-in-nagpur" },
        { title: "Dental Veneers", href: "/services/dental-veneers-in-nagpur" },
        { title: "Teeth Whitening", href: "/services/tooth-whitening-in-nagpur" },
        { title: "Laser Dentistry", href: "/services/laser-dentistry-in-nagpur" },
      ]
    },
    {
      category: "Restorative Dentistry",
      icon: <Sun className="h-4 w-4 mr-2" />,
      services: [
        { title: "Dental Implants", href: "/services/dental-implants-in-nagpur" },
        { title: "Crowns & Bridges", href: "/services/crowns-bridges-in-nagpur" },
        { title: "Single Day RCT", href: "/services/single-day-rct-in-nagpur" },
        { title: "Full Mouth & Implants", href: "/services/full-mouth-implants-in-nagpur" },
      ]
    },
    {
      category: "Pediatric Dentistry",
      icon: <Smile className="h-4 w-4 mr-2" />,
      services: [
        { title: "Pediatric Dentistry", href: "/services/pediatric-dentistry-in-nagpur" },
      ]
    },
    {
      category: "General Dentistry",
      icon: <Brackets className="h-4 w-4 mr-2" />,
      services: [
        { title: "General Dentistry", href: "/services/general-dentistry-in-nagpur" },
      ]
    },
  ];
  
  return (
    <header 
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white shadow-md py-2" : "bg-dental-offwhite py-4"
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/8ac72d70-a3d2-477b-af4a-21d4927e27a1.png" 
              alt="Asrani Dental Logo" 
              className="h-12" 
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="nav-link">
              Home
            </Link>
            <Link to="/about" className="nav-link">
              About
            </Link>
            
            {/* Services Dropdown - Enhanced with Navigation Menu and Categories */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="nav-link bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent">Services</NavigationMenuTrigger>
                  <NavigationMenuContent className="bg-white">
                    <div className="grid w-[550px] gap-3 p-4 md:w-[600px] lg:w-[700px] grid-cols-2">
                      {serviceCategories.map((category) => (
                        <div key={category.category} className="mb-4">
                          <h3 className="font-medium text-sm flex items-center text-dental-highlight mb-1">
                            {category.icon} {category.category}
                          </h3>
                          <ul className="space-y-1 pl-6">
                            {category.services.map((service) => (
                              <li key={service.href}>
                                <NavigationMenuLink asChild>
                                  <Link
                                    to={service.href}
                                    className="block select-none text-sm py-1.5 leading-none no-underline outline-none transition-colors hover:text-dental-highlight focus:text-dental-highlight"
                                  >
                                    {service.title}
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            <Link to="/clinics" className="nav-link">
              Clinics
            </Link>
            
            <Link to="/gallery" className="nav-link">
              Gallery
            </Link>
            <Link to="/blog" className="nav-link">
              Blogs
            </Link>
            <Link to="/contact" className="nav-link">
              <img src={smilebirdLogo} alt="Smilebird Health" className="h-10" />
            </Link>
          </nav>
          
          {/* Invisalign Provider Logo */}
          <div className="hidden md:block">
            <Link to="/services/invisalign-in-nagpur">
              <img 
                src="/lovable-uploads/a651ae05-51e6-43db-a41e-7a5227b29452.png" 
                alt="Invisalign Provider" 
                className="h-8 hover:opacity-80 transition-opacity cursor-pointer" 
              />
            </Link>
          </div>
          
          {/* Book Appointment Button */}
          <div className="hidden md:block">
            <Button className="btn-primary" asChild>
              <Link to="/dental-appointment-booking">Book Appointment</Link>
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-dental-text hover:text-dental-highlight transition-colors"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      
      {/* Mobile Sidebar */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={toggleMenu}
      />
      
      <div 
        className={`fixed top-0 right-0 bottom-0 w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } overflow-hidden flex flex-col`}
      >
        <div className="p-4 flex justify-between items-center border-b border-dental-cream">
          <img 
            src="/lovable-uploads/8ac72d70-a3d2-477b-af4a-21d4927e27a1.png" 
            alt="Asrani Dental Logo" 
            className="h-8" 
          />
          <button 
            className="text-dental-text hover:text-dental-highlight transition-colors"
            onClick={toggleMenu}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Mobile Navigation - Improved Layout */}
        <nav className="flex-1 overflow-y-auto">
          <div className="py-2 px-1">
            <Link to="/" className="block px-4 py-3 hover:bg-dental-cream rounded-md transition-colors">
              Home
            </Link>
            <Link to="/about" className="block px-4 py-3 hover:bg-dental-cream rounded-md transition-colors">
              About
            </Link>
            
            {/* Services Categories - Enhanced Mobile Experience */}
            <div className="mt-2 mb-2">
              <div className="px-4 pb-2 text-dental-highlight text-sm font-semibold uppercase tracking-wider">
                Services
              </div>
              
              <div className="rounded-md overflow-hidden">
                {serviceCategories.map((category) => (
                  <div key={category.category} className="border-b border-dental-cream last:border-b-0">
                    <button 
                      onClick={() => toggleCategory(category.category)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-dental-cream transition-colors text-left"
                    >
                      <span className="flex items-center font-medium">
                        {category.icon}
                        <span className="ml-2">{category.category}</span>
                      </span>
                      <ChevronDown 
                        size={18} 
                        className={`transition-transform duration-200 ${
                          expandedCategory === category.category ? 'rotate-180' : ''
                        }`} 
                      />
                    </button>
                    
                    <div 
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${
                        expandedCategory === category.category 
                          ? 'max-h-96 opacity-100' 
                          : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="bg-dental-offwhite/50 py-1">
                        {category.services.map((service) => (
                          <Link
                            key={service.href}
                            to={service.href}
                            className="flex px-6 py-2.5 hover:bg-dental-cream transition-colors text-sm"
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-dental-highlight mt-1.5 mr-2.5"></span>
                            {service.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <Link to="/clinics" className="block px-4 py-3 hover:bg-dental-cream rounded-md transition-colors">
              Clinics
            </Link>
            
            <Link to="/gallery" className="block px-4 py-3 hover:bg-dental-cream rounded-md transition-colors">
              Gallery
            </Link>
            
            <Link to="/blog" className="block px-4 py-3 hover:bg-dental-cream rounded-md transition-colors">
              Blogs
            </Link>
            
            <Link to="/contact" className="block px-4 py-3 hover:bg-dental-cream rounded-md transition-colors">
              <img src={smilebirdLogo} alt="Smilebird Health" className="h-8" />
            </Link>
          </div>
        </nav>
        
        {/* Mobile Footer Actions */}
        <div className="p-4 border-t border-dental-cream">
          <Button className="w-full btn-primary" asChild>
            <Link to="/dental-appointment-booking">Book Appointment</Link>
          </Button>
        </div>
      </div>
    </header>
  );
};
